/**
 * DimoY ecosystem product identity — CRA-safe vendored copy (DS-3).
 * Keep in sync with packages/design-system/products/ecosystem-products.js
 * Use existing product names; do not invent translations.
 */

/** @typedef {'internal' | 'external' | 'same-origin'} ProductLinkKind */

/**
 * @typedef {Object} EcosystemProduct
 * @property {string} id
 * @property {string} nameAr
 * @property {string} nameEn
 * @property {string} [descriptionAr]
 * @property {string} [descriptionEn]
 * @property {ProductLinkKind} linkKind
 * @property {string} [path] — same-origin path
 * @property {string} urlKey — key in getEcosystemUrls()
 * @property {string} category — workspace | legal | admin
 * @property {boolean} [requiresAuth]
 * @property {boolean} [openInNewTab]
 */

export const ECOSYSTEM_PRODUCTS = /** @type {EcosystemProduct[]} */ ([
  {
    id: "ydim",
    nameAr: "يمناك",
    nameEn: "YimnakAI",
    descriptionAr: "مساعد قانوني ذكي للأسئلة والاسترشاد.",
    descriptionEn: "Smart legal assistant for questions and guidance.",
    linkKind: "external",
    urlKey: "ydim",
    category: "workspace",
  },
  {
    id: "arsheef",
    nameAr: "أرشيف",
    nameEn: "Arsheef",
    descriptionAr: "إدارة القضايا والموكلين وسير عمل المكتب.",
    descriptionEn: "Case management, clients, and firm workflows.",
    linkKind: "external",
    urlKey: "arsheef",
    category: "workspace",
    requiresAuth: true,
  },
  {
    id: "ingestion",
    nameAr: "الأنظمة القانونية",
    nameEn: "Laws & Regulations",
    descriptionAr: "استعراض الأنظمة واللوائح والمواد القانونية.",
    descriptionEn: "Browse laws, regulations, and legal articles.",
    linkKind: "external",
    urlKey: "ingestion",
    category: "legal",
  },
  {
    id: "legal",
    nameAr: "مراجعة العقود باستخدام الذكاء الاصطناعي",
    nameEn: "AI Contract Review",
    descriptionAr: "مراجعة العقود وكشف المخاطر بالذكاء الاصطناعي.",
    descriptionEn: "AI-powered contract review and risk detection.",
    linkKind: "external",
    urlKey: "legal",
    category: "legal",
    requiresAuth: true,
  },
]);

export function isLocalRuntime() {
  return (
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1")
  );
}

/** @returns {Record<string, string>} */
export function getEcosystemUrls() {
  const local = isLocalRuntime();

  return {
    ydim: local ? "http://localhost:3000/" : "https://yimnakai.com/",
    arsheef: local
      ? "http://localhost:3001/login"
      : "https://arsheef.yimnakai.com/login",
    ingestion: local ? "http://localhost:5173/" : "https://ingestion.yimnakai.com/",
    legal: local
      ? "http://localhost:5174/legal-intelligence/"
      : "https://yimnakai.com/legal-intelligence/",
  };
}

/** @param {EcosystemProduct} product */
export function resolveProductHref(product) {
  if (product.linkKind === "same-origin" && product.path) {
    return product.path;
  }
  const urls = getEcosystemUrls();
  return urls[product.urlKey] || "#";
}

/** @param {EcosystemProduct} product @param {'ar' | 'en'} [locale='ar'] */
export function getProductDisplayName(product, locale = "ar") {
  return locale === "en" ? product.nameEn : product.nameAr;
}
